/**
 * Fallback data and utilities for analytics when real data is unavailable
 * Provides graceful degradation and placeholder content
 */

/**
 * Generate fallback analytics data for user dashboard
 */
export const generateFallbackUserAnalytics = (timeRange = 'billing_period') => {
  const now = new Date();
  const startDate = getTimeRangeStartDate(timeRange, now);
  
  return {
    timeRange: {
      start: startDate.toISOString(),
      end: now.toISOString(),
      type: timeRange,
      billingDaysRemaining: timeRange === 'billing_period' ? 15 : null
    },
    metrics: {
      totalCalls: 0,
      successfulCalls: 0,
      failedCalls: 0,
      successRate: 0,
      totalDuration: 0,
      averageDuration: 0,
      totalCost: 0,
      remainingQuota: {
        minutes: 1000 // Default quota
      }
    },
    callsByParrot: [],
    trends: {
      callVolume: generateEmptyTrendData(startDate, now),
      successRate: generateEmptyTrendData(startDate, now)
    },
    fallback: true,
    message: 'Analytics data is currently unavailable. This is placeholder data.'
  };
};

/**
 * Generate fallback call success analytics
 */
export const generateFallbackCallSuccessAnalytics = (timeRange = 'billing_period') => {
  return {
    overall: {
      totalCalls: 0,
      successfulCalls: 0,
      failedCalls: 0,
      successRate: 0
    },
    byParrot: [],
    byPhoneNumber: [],
    trends: [],
    fallback: true,
    message: 'Call success data is currently unavailable.'
  };
};

/**
 * Generate fallback failure reasons data
 */
export const generateFallbackFailureReasons = () => {
  return {
    failureBreakdown: [],
    fallback: true,
    message: 'Failure analysis data is currently unavailable.'
  };
};

/**
 * Generate fallback admin analytics
 */
export const generateFallbackAdminAnalytics = (timeRange = '7_days') => {
  const now = new Date();
  const startDate = getTimeRangeStartDate(timeRange, now);
  
  return {
    timeRange: {
      start: startDate.toISOString(),
      end: now.toISOString(),
      type: timeRange
    },
    platformMetrics: {
      totalUsers: 0,
      activeUsers: 0,
      totalCalls: 0,
      totalRevenue: 0,
      activeParrots: 0,
      callsToday: 0,
      revenueThisMonth: 0,
      userGrowth: {
        current: 0,
        previous: 0,
        percentChange: 0
      },
      callVolumeGrowth: {
        current: 0,
        previous: 0,
        percentChange: 0
      }
    },
    topPerformingParrots: [],
    trends: {
      userGrowth: generateEmptyTrendData(startDate, now),
      callVolume: generateEmptyTrendData(startDate, now),
      revenue: generateEmptyTrendData(startDate, now)
    },
    fallback: true,
    message: 'Admin analytics data is currently unavailable.'
  };
};

/**
 * Generate fallback system health data
 */
export const generateFallbackSystemHealth = () => {
  return {
    database: {
      status: 'unknown',
      connectionCount: 0,
      maxConnections: 100,
      storageUsed: 0,
      storageLimit: 10.0,
      queryPerformance: {
        averageResponseTime: 0,
        slowQueries: 0,
        errorRate: 0
      }
    },
    storage: {
      r2Buckets: [],
      totalUsage: 0,
      totalCost: 0
    },
    worker: {
      status: 'unknown',
      requestsPerMinute: 0,
      averageExecutionTime: 0,
      errorRate: 0,
      memoryUsage: 0,
      cpuUsage: 0
    },
    alerts: [],
    fallback: true,
    message: 'System health data is currently unavailable.'
  };
};

/**
 * Generate fallback conversations data
 */
export const generateFallbackConversations = (status = 'new') => {
  return {
    conversations: [],
    totalCount: 0,
    hasMore: false,
    fallback: true,
    message: `${status.charAt(0).toUpperCase() + status.slice(1)} conversations data is currently unavailable.`
  };
};

/**
 * Generate empty trend data for a date range
 */
const generateEmptyTrendData = (startDate, endDate) => {
  const data = [];
  const currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    data.push({
      date: currentDate.toISOString().split('T')[0],
      value: 0
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return data;
};

/**
 * Get start date for a given time range
 */
const getTimeRangeStartDate = (timeRange, endDate = new Date()) => {
  const start = new Date(endDate);
  
  switch (timeRange) {
    case '1_day':
      start.setDate(start.getDate() - 1);
      break;
    case '7_days':
      start.setDate(start.getDate() - 7);
      break;
    case '30_days':
      start.setDate(start.getDate() - 30);
      break;
    case '60_days':
      start.setDate(start.getDate() - 60);
      break;
    case '90_days':
      start.setDate(start.getDate() - 90);
      break;
    case 'month_to_date':
      start.setDate(1);
      break;
    case 'billing_period':
      // Assume 30-day billing cycle, 15 days remaining
      start.setDate(start.getDate() - 15);
      break;
    default:
      start.setDate(start.getDate() - 7);
  }
  
  return start;
};

/**
 * Create fallback content components for different analytics sections
 */
export const FallbackContent = {
  /**
   * Fallback message for metrics that couldn't load
   */
  MetricsUnavailable: ({ message = 'Metrics are currently unavailable' }) => (
    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
      <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      <p className="text-sm">{message}</p>
      <p className="text-xs mt-2 opacity-75">
        Data will appear here once the service is restored
      </p>
    </div>
  ),

  /**
   * Fallback for empty data sets
   */
  NoDataAvailable: ({ 
    title = 'No data available', 
    description = 'There is no data to display for the selected time period.',
    icon = 'chart'
  }) => {
    const icons = {
      chart: (
        <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      conversation: (
        <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      system: (
        <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    };

    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        {icons[icon]}
        <h3 className="text-lg font-medium mb-2">{title}</h3>
        <p className="text-sm">{description}</p>
      </div>
    );
  },

  /**
   * Fallback for service unavailable
   */
  ServiceUnavailable: ({ 
    serviceName = 'Analytics service',
    onRetry,
    canRetry = true
  }) => (
    <div className="text-center py-12">
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 max-w-md mx-auto">
        <svg className="w-12 h-12 mx-auto mb-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200 mb-2">
          {serviceName} Unavailable
        </h3>
        <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-4">
          The service is temporarily unavailable. Please try again in a few moments.
        </p>
        {canRetry && onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors dark:text-white"
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  )
};

/**
 * Check if data is fallback data
 */
export const isFallbackData = (data) => {
  return data && data.fallback === true;
};

/**
 * Get fallback message from data
 */
export const getFallbackMessage = (data) => {
  return data?.message || 'Data is currently unavailable';
};

/**
 * Merge real data with fallback data
 * Useful for partial data loading scenarios
 */
export const mergeWithFallback = (realData, fallbackData) => {
  if (!realData) return fallbackData;
  if (!fallbackData) return realData;
  
  return {
    ...fallbackData,
    ...realData,
    fallback: false
  };
};

/**
 * Create a fallback data provider hook
 */
export const useFallbackData = (dataType, timeRange) => {
  const getFallbackData = () => {
    switch (dataType) {
      case 'user_analytics':
        return generateFallbackUserAnalytics(timeRange);
      case 'call_success':
        return generateFallbackCallSuccessAnalytics(timeRange);
      case 'failure_reasons':
        return generateFallbackFailureReasons();
      case 'admin_analytics':
        return generateFallbackAdminAnalytics(timeRange);
      case 'system_health':
        return generateFallbackSystemHealth();
      case 'conversations':
        return generateFallbackConversations();
      default:
        return null;
    }
  };

  return { getFallbackData };
};
